import { countryTiersOnStage, PLAN_MAX_TIER } from '@simplyanvil/cl-types';
import type { OrganisationPlan, Stage } from '@simplyanvil/cl-types';
import { ALL_COUNTRIES } from '@/data/countries';
import { currentStage } from '@/libs/stage';

export type PlanTier = 'Lite' | 'Plus' | 'Enterprise';

// Every plan an organisation can be on, lowest tier first. Free is the implicit
// default for an org without a plan; Enterprise Plus is the multi-entity parent.
export const ORGANISATION_PLANS: readonly OrganisationPlan[] = ['Free', 'Lite', 'Plus', 'Enterprise', 'Enterprise Plus'];

const PLAN_RANK: Record<OrganisationPlan, number> = { Free: 0, Lite: 1, Plus: 2, Enterprise: 3, 'Enterprise Plus': 4 };

const toPlan = (plan?: string | null): OrganisationPlan =>
  plan && plan in PLAN_RANK ? (plan as OrganisationPlan) : 'Free';

// True when an org on `plan` (unset or unknown = Free) has at least the `minimum` tier.
export const hasMinimumPlan = (plan: string | null | undefined, minimum: OrganisationPlan): boolean =>
  PLAN_RANK[toPlan(plan)] >= PLAN_RANK[minimum];

export const isPaidTier = (plan?: string | null): plan is 'Lite' | 'Plus' => plan === 'Lite' || plan === 'Plus';

export interface PlanFeature {
  label: string;
  included: boolean;
}

export interface PlanQuotas {
  docsPerMonth: number | null; // null = no fixed limit
  storageGb: number | null;
}

export interface Plan {
  tier: PlanTier;
  name: string;
  tagline: string;
  priceLabel: string; // display only; real price lives in Stripe
  priceSuffix: string;
  billingNote: string;
  docsLabel: string;
  storageLabel: string;
  quotas: PlanQuotas;
  cta: 'checkout' | 'contact';
  highlighted: boolean;
  featuresIntro?: string;
  features: PlanFeature[];
}

export const PLANS: Plan[] = [
  {
    tier: 'Lite',
    name: 'Lite',
    tagline: 'Get compliant without writing code',
    priceLabel: '€99',
    priceSuffix: '/ month',
    billingNote: 'per VAT number, billed monthly',
    docsLabel: '100 documents / month',
    storageLabel: '1 GB archive storage',
    quotas: { docsPerMonth: 100, storageGb: 1 },
    cta: 'checkout',
    highlighted: false,
    features: [
      { label: 'Unlimited users', included: true },
      { label: 'Legal compliance archiving', included: true },
      { label: 'CSV upload with easy mapping tool', included: true },
      { label: 'Manual invoice & credit note creation', included: true },
      { label: 'Support Knowledge Hub', included: true }
    ]
  },
  {
    tier: 'Plus',
    name: 'Plus',
    tagline: 'Automate with the API and let AI fix errors',
    priceLabel: '€169',
    priceSuffix: '/ month',
    billingNote: 'per VAT number, billed monthly',
    docsLabel: '1,000 documents / month',
    storageLabel: '2 GB archive storage',
    quotas: { docsPerMonth: 1000, storageGb: 2 },
    cta: 'checkout',
    highlighted: true,
    featuresIntro: 'All of Lite, and',
    features: [
      { label: 'Full API access', included: true },
      { label: 'AI error handling', included: true },
      { label: 'Automated daily activity reports', included: true }
    ]
  },
  {
    tier: 'Enterprise',
    name: 'Enterprise',
    tagline: 'Many entities, one contract',
    priceLabel: 'Custom',
    priceSuffix: '',
    billingNote: 'volume pricing across all your entities',
    docsLabel: 'Unlimited documents',
    storageLabel: 'Storage sized to your archive',
    quotas: { docsPerMonth: null, storageGb: null },
    cta: 'contact',
    highlighted: false,
    featuresIntro: 'All of Plus, and',
    features: [
      { label: 'Multi-entity management', included: true },
      { label: 'Single sign-on (SSO)', included: true },
      { label: 'Access to tax experts', included: true },
      { label: 'Real-time input language translation', included: true }
    ]
  }
];

// Free tier is not on the pricing cards: 5 docs / month, 200 MB storage
export const FREE_MONTHLY_DOC_LIMIT = 5;
const FREE_QUOTAS: PlanQuotas = { docsPerMonth: FREE_MONTHLY_DOC_LIMIT, storageGb: 200 / 1024 };

// Usage quotas for an org's plan value; Enterprise (Plus) has no fixed limit
export const getPlanQuotas = (plan?: string | null): PlanQuotas => {
  const known = toPlan(plan);
  const tier = known === 'Enterprise Plus' ? 'Enterprise' : known;
  if (tier === 'Free') return FREE_QUOTAS;
  return PLANS.find((p) => p.tier === tier)?.quotas ?? { docsPerMonth: null, storageGb: null };
};

export const countryName = (iso3: string): string => ALL_COUNTRIES.find((c) => c.code === iso3)?.name ?? iso3;

const onStage = (stage: Stage) => Object.entries(countryTiersOnStage(stage));

export const countriesForPlan = (plan: PlanTier, stage: Stage = currentStage()): string[] =>
  onStage(stage)
    .filter(([, v]) => v.tier !== null && v.tier <= PLAN_MAX_TIER[plan])
    .map(([iso3]) => iso3)
    .sort();

export const countriesAddedByPlan = (plan: 'Plus' | 'Enterprise', stage: Stage = currentStage()): string[] =>
  onStage(stage)
    .filter(([, v]) => v.tier === PLAN_MAX_TIER[plan])
    .map(([iso3]) => iso3)
    .sort();

export const unclassifiedCountries = (stage: Stage = currentStage()): string[] =>
  onStage(stage)
    .filter(([, v]) => v.tier === null)
    .map(([iso3]) => iso3)
    .sort();

export const displayName = (iso3: string, stage: Stage = currentStage()): string => {
  const note = countryTiersOnStage(stage)[iso3]?.note;
  return note ? `${countryName(iso3)} (${note})` : countryName(iso3);
};

export const sortByDisplayName = (list: string[], stage: Stage = currentStage()): string[] =>
  [...list].sort((a, b) => displayName(a, stage).localeCompare(displayName(b, stage)));

const names = (list: string[], stage: Stage): string =>
  sortByDisplayName(list, stage).map((iso3) => displayName(iso3, stage)).join(', ');

export const countriesIntro = (plan: PlanTier, stage: Stage = currentStage()): string => {
  if (plan === 'Lite') return `Included countries: ${names(countriesForPlan('Lite', stage), stage)} — the same as Free`;
  if (plan === 'Plus') return `All Lite countries plus ${names(countriesAddedByPlan('Plus', stage), stage)}`;
  return 'Every country we cover';
};
